import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';
import { notification } from 'antd';
import { store } from '../store';
import { AUTH_TOKEN } from 'constants/AuthConstant';


const sessionExpiredCodes = [401, 419];
let sessionExpiredNotificationShown = false; 

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

const castService = axios.create({
  baseURL: 'https://cast-api.unmutedte.ch',
  timeout: 60000,
});

const TOKEN_PAYLOAD_KEY = 'Authorization';

const isAuthEndpoint = (url = '') =>
  url.includes('/authenticate');

const expireSession = (status) => {
  if (sessionExpiredNotificationShown) {
    return;
  }

  sessionExpiredNotificationShown = true;

  const notificationParam = status === 419
    ? {
        message: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
      }
    : {
        message: 'Session Expired',
        description: 'Please log in again to continue.',
      };

  notification.error(notificationParam);
  localStorage.removeItem(AUTH_TOKEN);
  store.dispatch({ type: 'auth/showLoading', payload: false });
  store.dispatch({ type: 'auth/signOut/fulfilled' });
};

const attachAuthToken = (config) => {
  const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

  if (jwtToken) {
    config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
  }

  return config;
};

const handleRequestError = (error) => {
  notification.error({
    message: 'Error',
    description: 'There was an error with the request.',
  });
  return Promise.reject(error);
};

const handleResponseError = (error) => {
  const status = error.response?.status;
  const requestUrl = error.config?.url || '';

  if (isAuthEndpoint(requestUrl)) {
    return Promise.reject(error);
  }

  if (status && sessionExpiredCodes.includes(status)) {
    expireSession(status);
    return Promise.reject(error);
  }

  if (error.config?.disableErrorNotification) {
    return Promise.reject(error);
  }

  if (!error.response) {
    notification.error({
      message: 'Network Error',
      description: 'Unable to reach the server. Please check your connection and try again.',
    });
    return Promise.reject(error);
  }

  if (status === 404) {
    return Promise.reject(error);
  }

  const notificationParam = {
    message: 'Error',
    description: error.response?.data?.message || error.message,
  };

  if (status === 400) {
    notificationParam.message = 'Request Failed';
    notificationParam.description = error.response?.data?.message || 'Please check your details and try again.';
  }

  if (status === 403) {
    notificationParam.message = 'Permission Denied';
    notificationParam.description = 'You do not have access to this resource.';
  }

  if (status === 500) {
    notificationParam.message = 'Internal Server Error';
    notificationParam.description = 'An error occurred on the server. Please try again later.';
  }

  if (status === 508) {
    notificationParam.message = 'Time Out';
    notificationParam.description = 'The server took too long to respond. Please try again later.';
  }

  notification.error(notificationParam);
  return Promise.reject(error);
};

service.interceptors.request.use(attachAuthToken, handleRequestError);
castService.interceptors.request.use(attachAuthToken, handleRequestError);

service.interceptors.response.use(
  (response) => {
    sessionExpiredNotificationShown = false;
    return response.data;
  },
  handleResponseError
);

castService.interceptors.response.use(
  (response) => {
    sessionExpiredNotificationShown = false;
    return response;  
  },
  handleResponseError
);


export { service as default, castService };
